/**
 * Client-side PDF Helper Functions
 * Pure utility functions ported from server/generators/utils/pdfHelpers.js
 * No Node.js dependencies — safe to run in the browser.
 */

import { StyleSheet } from '@react-pdf/renderer';

// ---------------------------------------------------------------------------
// Color tokens
// ---------------------------------------------------------------------------
export const COLORS = {
  primary:     '#005A8C',
  accent:      '#3498db',
  normal:      '#000000', // normal findings print in black; only abnormal ones stand out (red)
  abnormal:    '#e74c3c',
  lightGray:   '#ecf0f1',
  lightBlue:   '#e3f2fd',
  lightYellow: '#fff9e6',
  callAlert:   '#fff3cd',
  callBorder:  '#ff6b6b',
  callText:    '#721c24',
  darkHeader:  '#2c3e50',
  white:       '#ffffff',
  black:       '#000000',
};

// ---------------------------------------------------------------------------
// Shared StyleSheet — imported by every generator
// ---------------------------------------------------------------------------
export const commonStyles = StyleSheet.create({
  page: {
    paddingTop: 10,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: COLORS.black,
  },

  // ── Header ──────────────────────────────────────────────────────────────
  headerTopBorder: {
    height: 3,
    backgroundColor: COLORS.primary,
    marginBottom: 6,
  },
  facilityName: {
    color: COLORS.primary,
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    marginBottom: 4,
  },
  titleBar: {
    backgroundColor: COLORS.primary,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 8,
  },
  titleBarText: {
    color: COLORS.white,
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },

  // ── Patient Info Box ─────────────────────────────────────────────────────
  patientBox: {
    border: `2px solid ${COLORS.primary}`,
    borderRadius: 5,
    backgroundColor: COLORS.lightGray,
    padding: 10,
    marginBottom: 8,
  },
  patientBoxTitle: {
    color: COLORS.primary,
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
  },
  patientRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  patientCell: {
    flex: 1,
    flexDirection: 'row',
  },
  patientLabel: {
    fontFamily: 'Helvetica-Bold',
    marginRight: 3,
    fontSize: 10,
  },
  patientValue: {
    fontSize: 10,
    flex: 1,
  },

  // ── Call Report Banner ───────────────────────────────────────────────────
  callReportBox: {
    border: `1px solid ${COLORS.callBorder}`,
    borderRadius: 5,
    backgroundColor: COLORS.callAlert,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginBottom: 8,
  },
  callReportText: {
    color: COLORS.callText,
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    textAlign: 'center',
  },

  // ── Section Headers ───────────────────────────────────────────────────────
  sectionHeader: {
    color: COLORS.darkHeader,
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    borderBottom: `2px solid ${COLORS.darkHeader}`,
    paddingBottom: 2,
    marginBottom: 5,
    marginTop: 8,
  },
  accentSectionHeader: {
    color: COLORS.accent,
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    borderBottom: `2px solid ${COLORS.accent}`,
    paddingBottom: 2,
    marginBottom: 5,
    marginTop: 8,
  },

  // ── Label / Value rows ───────────────────────────────────────────────────
  row: {
    flexDirection: 'row',
    marginBottom: 3,
    flexWrap: 'wrap',
  },
  label: {
    fontFamily: 'Helvetica-Bold',
    marginRight: 3,
    fontSize: 10,
  },
  valueNormal: {
    color: COLORS.normal,
    fontSize: 10,
  },
  valueAbnormal: {
    color: COLORS.abnormal,
    fontSize: 10,
  },
  valueDefault: {
    fontSize: 10,
  },

  // ── Measurement / Info boxes ─────────────────────────────────────────────
  measurementBox: {
    border: `1px solid ${COLORS.accent}`,
    borderRadius: 5,
    backgroundColor: COLORS.lightBlue,
    padding: 8,
    marginBottom: 8,
  },
  commentsBox: {
    border: `1px solid #f39c12`,
    borderRadius: 5,
    backgroundColor: COLORS.lightYellow,
    padding: 8,
    marginBottom: 8,
  },
  infoBoxText: {
    fontSize: 10,
    lineHeight: 1.5,
  },

  // ── Two-column layout ────────────────────────────────────────────────────
  twoCol: {
    flexDirection: 'row',
    gap: 12,
  },
  colLeft: {
    flex: 1,
  },
  colRight: {
    flex: 1,
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    borderTop: `1px solid ${COLORS.lightGray}`,
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#888',
  },
});

// ---------------------------------------------------------------------------
// Pure helper functions (no Node.js dependencies)
// ---------------------------------------------------------------------------

/**
 * Convert history field to string.
 * Handles both string history and array history (Renal form).
 */
export function historyToString(history) {
  if (!history) return '';
  if (Array.isArray(history)) {
    return history.filter(h => h && h.trim()).join(', ').trim();
  }
  return String(history).trim();
}

/**
 * Calculate organ volume: X * Y * Z * 0.52
 */
export function calculateVolume(x, y, z) {
  const volume = parseFloat(x) * parseFloat(y) * parseFloat(z) * 0.52;
  return isNaN(volume) ? '0.0' : volume.toFixed(1);
}

/**
 * Format an ovary's adnexal description using a volume-based placement
 * rule (First/Second Trimester OB): the size always appears inline in the
 * Adnexa/Maternal Structures description, e.g. "Normal (2 x 3 x 2 cm;
 * 6.2 mL)". A normal-size ovary (< 20 mL) stops there. An enlarged ovary
 * (>= 20 mL) additionally gets a duplicate line in the Measurements
 * section (an abnormal-size finding deserves top billing there too), and
 * `sizeAbnormal` comes back true so the caller can flag the Adnexa line
 * itself as abnormal (red) even when the status text alone reads
 * "Normal" — the size is the abnormal part, so it shouldn't render in the
 * default color with no visual flag.
 * Shared by FirstTrimesterOBPDF.js and SecondTrimesterMultipleOBPDF.js so
 * the two never drift.
 * @param {string} label - e.g. 'Right ovary' — used as the prefix on the
 *   duplicate measurement line for an enlarged ovary.
 * @param {string} statusText - the ovary's status/finding description.
 * @param {string|number} length
 * @param {string|number} width
 * @param {string|number} depth
 * @returns {{ adnexaText: string, measurementLine: string|null, sizeAbnormal: boolean }}
 */
export function formatOvaryAdnexa(label, statusText, length, width, depth) {
  const hasAllDims = length && width && depth;
  if (!hasAllDims) {
    return { adnexaText: statusText, measurementLine: null, sizeAbnormal: false };
  }
  const volume = calculateVolume(length, width, depth);
  const volNum = parseFloat(volume);
  const sizeText = `${length} x ${width} x ${depth} cm; ${volume} mL`;
  const enlarged = !isNaN(volNum) && volNum >= 20;
  return {
    adnexaText: `${statusText} (${sizeText})`,
    measurementLine: enlarged ? `${label}: ${sizeText}` : null,
    sizeAbnormal: enlarged
  };
}

/**
 * Return history string, or 'None' if empty.
 */
export function formatHistory(history) {
  if (!history || String(history).trim().length === 0) return 'None';
  return String(history).trim();
}

/**
 * Join an array of items as a comma-separated string.
 */
export function formatListItems(items) {
  if (!items || items.length === 0) return '';
  return items.filter(Boolean).join(', ');
}

/**
 * Format a date string to mm/dd/yyyy.
 */
export function formatExamDate(dateString) {
  if (!dateString) {
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return dateString;
  // YYYY-MM-DD (what <input type="date"> produces) — parse the parts directly
  // rather than via `new Date(...)`, which treats date-only strings as UTC
  // midnight; formatting that with local-time getters rolls the date back by
  // one day in any timezone behind UTC (i.e. all of the US).
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${mm}/${dd}/${yyyy}`;
  }
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  return dateString;
}

/**
 * Build combined reason/history string for patient info box.
 */
export function buildCombinedReason(reason, history) {
  const parts = [];
  if (reason && String(reason).trim()) parts.push(String(reason).trim());
  const histStr = historyToString(history);
  if (histStr) parts.push(histStr);
  if (parts.length === 0) return 'Unspecified';
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('. ');
}

/**
 * Return the facility name from a facilityInfo object,
 * with a sensible fallback.
 */
export function getFacilityDisplayName(facilityInfo) {
  return (facilityInfo && facilityInfo.name) ? facilityInfo.name : 'Fairbanks Memorial Hospital';
}

/**
 * Build a concise DVT findings summary for one side (leg/arm).
 * veinLabels: { fieldKey: 'Display label' } — order defines list order.
 * sideData: the form data object for this side (e.g. data.rightLeg).
 * sideLabel: 'Right' | 'Left'. extremityLabel: 'lower' | 'upper'.
 * Returns an array of { text, tone } lines. tone is 'normal' | 'abnormal' | 'neutral'.
 */
export function buildDVTSummaryLines(veinLabels, sideData, sideLabel, extremityLabel) {
  const acuteVeins = [];
  const chronicVeins = [];
  const nwvVeins = [];

  Object.entries(veinLabels).forEach(([key, label]) => {
    const status = (sideData[key] && sideData[key].status) || '';
    if (status === 'Acute DVT' || status === 'Partial thrombosis' || status === 'Acute & Chronic DVT') acuteVeins.push(label);
    if (status === 'Chronic DVT' || status === 'Acute & Chronic DVT') chronicVeins.push(label);
    if (status === 'Not well visualized') nwvVeins.push(label);
  });

  const lines = [];

  if (acuteVeins.length > 0) {
    lines.push({ text: `Positive for acute DVT involving the following veins: ${acuteVeins.join(', ')}.`, tone: 'abnormal' });
    if (chronicVeins.length > 0) {
      lines.push({ text: `Chronic changes also noted in: ${chronicVeins.join(', ')}.`, tone: 'abnormal' });
    }
  } else if (chronicVeins.length > 0) {
    lines.push({ text: `No acute DVT. Findings of chronic DVT are present, involving the following veins: ${chronicVeins.join(', ')}.`, tone: 'abnormal' });
  } else {
    lines.push({ text: `No DVT in the ${sideLabel.toLowerCase()} ${extremityLabel} extremity.`, tone: 'normal' });
  }

  if (nwvVeins.length > 0) {
    lines.push({ text: `${nwvVeins.join(', ')} not well visualized.`, tone: 'neutral' });
  }

  return lines;
}

// ACR TI-RADS 2017 nodule descriptor options with their point values.
// Shared by ThyroidForm.js (dropdown options + score calculation) and
// ThyroidPDF.js (the PDF's compact "descriptor (points)" summary line) so
// the two can never disagree about what a given descriptor is worth.
export const NODULE_COMPOSITION = [
  { label: 'Cystic (0)', value: 'Cystic', points: 0 },
  { label: 'Spongiform (0)', value: 'Spongiform', points: 0 },
  { label: 'Mixed solid/cystic (1)', value: 'Mixed solid/cystic', points: 1 },
  { label: 'Solid (2)', value: 'Solid', points: 2 }
];

export const NODULE_ECHOGENICITY = [
  { label: 'Anechoic (0)', value: 'Anechoic', points: 0 },
  { label: 'Hyperechoic (1)', value: 'Hyperechoic', points: 1 },
  { label: 'Isoechoic (1)', value: 'Isoechoic', points: 1 },
  { label: 'Hypoechoic (2)', value: 'Hypoechoic', points: 2 },
  { label: 'Markedly hypoechoic (3)', value: 'Markedly hypoechoic', points: 3 }
];

export const NODULE_MARGINS = [
  { label: 'Smooth (0)', value: 'Smooth', points: 0 },
  { label: 'Ill-defined (0)', value: 'Ill-defined', points: 0 },
  { label: 'Irregular or lobulated (2)', value: 'Irregular or lobulated', points: 2 },
  { label: 'Extra-thyroidal extension (3)', value: 'Extra-thyroidal extension', points: 3 }
];

export const NODULE_SHAPE = [
  { label: 'Wider than tall (0)', value: 'Wider than tall', points: 0 },
  { label: 'Taller than wide (3)', value: 'Taller than wide', points: 3 }
];

export const ECHOGENIC_FOCI = [
  { label: 'None (0)', value: 'None', points: 0 },
  { label: 'Macrocalcifications (1)', value: 'Macrocalcifications', points: 1 },
  { label: 'Peripheral/rim calcifications (2)', value: 'Peripheral/rim calcifications', points: 2 },
  { label: 'Punctate echogenic foci (3)', value: 'Punctate echogenic foci', points: 3 }
];

/**
 * True once at least one of the five scored TI-RADS descriptors has been
 * chosen. Until then the stored score is just the initial 0, which would
 * otherwise read as a real "TR-1 Benign" result. Shared by the form and PDF.
 */
export function hasTIRADSFeatures(nodule) {
  return !!(nodule && (nodule.composition || nodule.echogenicity || nodule.margins
    || nodule.shape || nodule.echogenicFoci));
}

/**
 * Shared score-to-category boundaries for both formatTIRADSCategory() and
 * formatTIRADSCompact() below, so the two wordings can never disagree about
 * which category a given point total falls into. Returns null only for an
 * invalid score (negative / non-numeric). ACR's published table lists TR1 = 0
 * and TR2 = 2 points and never assigns 1 point (easily reached, e.g. Mixed
 * solid/cystic alone), so 1 is treated as TR2 here, matching common calculators.
 */
function getTIRADSCategoryParts(score) {
  if (score === 0) return { trNumber: 1, description: 'Benign' };
  if (score === 1 || score === 2) return { trNumber: 2, description: 'Not Suspicious' };
  if (score === 3) return { trNumber: 3, description: 'Mildly Suspicious' };
  if (score >= 4 && score <= 6) return { trNumber: 4, description: 'Moderately Suspicious' };
  if (score >= 7) return { trNumber: 5, description: 'Highly Suspicious' };
  return null;
}

/**
 * Format an ACR TI-RADS point total as its category string, e.g.
 * "Total Points 4: TR-4 - Moderately Suspicious". Shared by ThyroidForm.js
 * (form display) and ThyroidPDF.js (PDF output) so the two never drift —
 * the PDF previously showed only the raw point total with no category.
 */
export function formatTIRADSCategory(score) {
  const parts = getTIRADSCategoryParts(score);
  if (!parts) return 'Unknown';
  return `Total Points ${score}: TR-${parts.trNumber} - ${parts.description}`;
}

/**
 * Terser TI-RADS category wording for space-constrained contexts (the PDF's
 * per-nodule summary, where several nodules may need to fit in one box),
 * e.g. "TI-RADS 5 (9 points) - Highly Suspicious". Same category boundaries
 * as formatTIRADSCategory() — just different wording, not a different rule.
 */
export function formatTIRADSCompact(score) {
  const parts = getTIRADSCategoryParts(score);
  if (!parts) return 'Unknown';
  return `TI-RADS ${parts.trNumber} (${score} point${score === 1 ? '' : 's'}) - ${parts.description}`;
}

/**
 * ACR TI-RADS 2017 (Tessler et al.) size-based management recommendation.
 * TR1/TR2 never need follow-up regardless of size. TR3/4/5 each have their
 * own FNA and surveillance-follow-up size thresholds (cm, largest nodule
 * dimension). Returns null when the category is unrecognized (e.g. a score
 * of 3+ with no size recorded yet, or an invalid score), since no
 * recommendation can be made without both inputs.
 */
export function getTIRADSRecommendation(score, nodule) {
  if (score === 0 || score === 1 || score === 2) return 'No follow-up needed';

  let fnaThreshold, followUpThreshold;
  if (score === 3) { fnaThreshold = 2.5; followUpThreshold = 1.5; }
  else if (score >= 4 && score <= 6) { fnaThreshold = 1.5; followUpThreshold = 1.0; }
  else if (score >= 7) { fnaThreshold = 1.0; followUpThreshold = 0.5; }
  else return null;

  const dims = [nodule && nodule.length, nodule && nodule.width, nodule && nodule.depth]
    .map(v => parseFloat(v))
    .filter(v => !isNaN(v));
  if (dims.length === 0) return null;
  const maxDim = Math.max(...dims);

  if (maxDim >= fnaThreshold) return 'Recommend FNA';
  if (maxDim >= followUpThreshold) return 'Recommend sonographic follow-up';
  return 'No follow-up needed';
}
